import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import api from "services/api";
import { requireAuth } from "actions/AuthAction";
import UserFormDialog from "./components/UserFormDialog";

const roleColor = {
  ADMIN: "info",
  PETUGAS: "primary",
  VERIFIKATOR: "warning",
  PIMPINAN: "success",
};

function AdminUsers() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [selectedUser, setSelectedUser] = useState(null);

  const loadData = useCallback(() => {
    setLoading(true);
    api
      .get("users")
      .then((res) => setRows(res.data?.data || []))
      .catch(() => alert("Gagal memuat daftar user"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!requireAuth(navigate, ["SUPER_ADMIN"])) return;
    loadData();
  }, [navigate, loadData]);

  const openCreate = () => {
    setFormMode("create");
    setSelectedUser(null);
    setFormOpen(true);
  };

  const openEdit = (user) => {
    setFormMode("edit");
    setSelectedUser(user);
    setFormOpen(true);
  };

  const handleDeactivate = async (user) => {
    if (!window.confirm(`Nonaktifkan akun "${user.username}" (${user.role})?`)) return;
    try {
      await api.delete(`users/${user.id}`);
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || "Gagal menonaktifkan user");
    }
  };

  const columns = [
    { Header: "Username", accessor: "username", width: "12%" },
    { Header: "Nama", accessor: "nama", width: "18%" },
    { Header: "Email", accessor: "email", width: "18%" },
    { Header: "Role", accessor: "role", width: "12%" },
    { Header: "Status", accessor: "status", width: "10%" },
    { Header: "Instansi", accessor: "instansi", width: "16%" },
    { Header: "Aksi", accessor: "action", align: "center" },
  ];

  const tableRows = rows.map((u) => ({
    username: u.username,
    nama: u.nama,
    email: u.email,
    role: <Chip label={u.role} size="small" color={roleColor[u.role] || "default"} />,
    status: (
      <Chip label={u.status} size="small" color={u.status === "AKTIF" ? "success" : "default"} />
    ),
    instansi: u.instansi || "-",
    action: (
      <MDBox display="flex" gap={0.5} justifyContent="center" flexWrap="wrap">
        <MDButton size="small" color="info" variant="text" onClick={() => openEdit(u)}>
          Edit
        </MDButton>
        {u.status === "AKTIF" ? (
          <MDButton size="small" color="error" variant="text" onClick={() => handleDeactivate(u)}>
            Nonaktifkan
          </MDButton>
        ) : null}
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
          <MDBox>
            <MDTypography variant="h4">Kelola User Staff</MDTypography>
            <MDTypography variant="button" color="text">
              Super Admin — kelola akun Admin, Petugas, Verifikator, dan Pimpinan
            </MDTypography>
          </MDBox>
          <MDButton variant="gradient" color="info" onClick={openCreate}>
            + Tambah User Staff
          </MDButton>
        </MDBox>

        <Card>
          <MDBox p={3}>
            {loading ? (
              <MDTypography>Memuat...</MDTypography>
            ) : (
              <DataTable table={{ columns, rows: tableRows }} canSearch entriesPerPage={{ defaultValue: 15 }} />
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />

      <UserFormDialog
        open={formOpen}
        mode={formMode}
        user={selectedUser}
        onClose={() => setFormOpen(false)}
        onSaved={loadData}
      />
    </DashboardLayout>
  );
}

export default AdminUsers;
